// Entrada al panel.
//
// ⭐ SABE VOLVER A DONDE VENÍAS (`?next=`). Sin eso, el caso más común se rompía
// en silencio: quien YA tiene cuenta —los asesores de Living House lo son— abre
// su enlace de invitación, ve que hay que iniciar sesión, entra… y aterriza en
// el inbox. El vale se quedó atrás y la persona cree que la invitación no sirve.
// El destino se valida antes de usarlo (`destinoSeguro`): aceptar cualquier
// `next` sería una redirección abierta — un enlace NUESTRO que lleva a otro
// sitio, justo lo que necesita un phishing creíble.
//
// ⭐⭐ Y PIDE EL SEGUNDO FACTOR. Cristhian activó TOTP en su cuenta el 2-ago y el
// panel no lo pedía: `signInWithPassword` devuelve una sesión de nivel `aal1`
// —válida— y aquí no había nada que exigiera subir a `aal2`. O sea que su 2FA
// estaba puesto y NO protegía Chat AI: bastaba la contraseña. Ahora, si la cuenta
// tiene un factor verificado, hay que escribir el código; y si no se completa,
// **se cierra la sesión** en vez de dejar entrar a medias.
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Aviso, Boton } from "@victorix/chat-ai-ui";
import { sb } from "../lib/supabase.ts";
import { destinoSeguro } from "../lib/destino.ts";
import { mensajeDeErrorAuth } from "../lib/errores-auth.ts";
import { Campo, CampoClave, MarcoAuth } from "./_marco.tsx";

export default function Login() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const [correo, setCorreo] = useState(params.get("correo") ?? "");
  const [clave, setClave] = useState("");
  const [ver, setVer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);

  // Segundo paso: el código del autenticador.
  const [factorId, setFactorId] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");

  const next = destinoSeguro(params.get("next"));

  function terminar() {
    // Si no hay destino, el ProveedorSesion re-renderiza solo al cambiar la sesión.
    if (next !== "/") navegar(next, { replace: true });
  }

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    setEntrando(true); setError(null);

    const { error: errEntrar } = await sb.auth.signInWithPassword({
      email: correo.trim(), password: clave,
    });
    if (errEntrar) { setError(mensajeDeErrorAuth(errEntrar)); setEntrando(false); return; }

    // ¿Esta cuenta exige subir a aal2? Lo decide Supabase, no nosotros.
    const { data: nivel } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
    if (nivel?.nextLevel === "aal2" && nivel.nextLevel !== nivel.currentLevel) {
      const { data: factores } = await sb.auth.mfa.listFactors();
      const totp = factores?.totp?.[0];
      if (!totp) {
        // Pide un factor y no hay ninguno usable: no se deja media sesión abierta.
        await sb.auth.signOut();
        setError("Tu cuenta pide un segundo factor pero no encontramos el autenticador. "
          + "Escríbele a Victorix.");
        setEntrando(false);
        return;
      }
      setFactorId(totp.id);
      setEntrando(false);
      return;                       // → segundo paso
    }

    terminar();
  }

  async function verificar(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    setEntrando(true); setError(null);

    const { data: reto, error: errReto } = await sb.auth.mfa.challenge({ factorId });
    if (errReto || !reto) { setError(mensajeDeErrorAuth(errReto)); setEntrando(false); return; }

    const { error: errVerif } = await sb.auth.mfa.verify({
      factorId, challengeId: reto.id, code: codigo.trim(),
    });
    if (errVerif) {
      setError(/invalid|incorrect|expired/i.test(errVerif.message ?? "")
        ? "Ese código no es válido o ya venció. Mira el que aparece ahora en tu app."
        : mensajeDeErrorAuth(errVerif));
      setCodigo("");
      setEntrando(false);
      return;
    }
    terminar();
  }

  /** Volver atrás sin dejar la sesión a medias: `signInWithPassword` ya abrió una
   *  de nivel aal1, y salir sin cerrarla la dejaría viva en el navegador. */
  async function cancelarSegundoPaso() {
    await sb.auth.signOut();
    setFactorId(null); setCodigo(""); setClave(""); setError(null);
  }

  if (factorId) {
    return (
      <MarcoAuth
        titulo="Escribe tu código"
        bajada="El de 6 dígitos que muestra tu app de autenticación."
      >
        <form onSubmit={verificar}>
          <Campo
            id="codigo" etiqueta="Código" type="text" inputMode="numeric"
            autoComplete="one-time-code" required autoFocus
            pattern="[0-9]*" maxLength={6} value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
            ayuda="Cambia cada 30 segundos: usa el que se ve ahora."
          />

          {error && <div className="mt-4"><Aviso tono="peligro">{error}</Aviso></div>}

          <Boton type="submit" tono="marca" className="mt-6 w-full"
                 disabled={entrando || codigo.length < 6}>
            {entrando ? "Comprobando…" : "Entrar"}
          </Boton>

          <p className="mt-4 text-center">
            <button type="button" onClick={cancelarSegundoPaso}
              className="text-[13px] text-tinta-2 hover:text-marca transition cursor-pointer">
              Usar otra cuenta
            </button>
          </p>
        </form>
      </MarcoAuth>
    );
  }

  return (
    <MarcoAuth titulo="Entrar" bajada="Con tu cuenta de Victorix.">
      <form onSubmit={entrar}>
        <div className="space-y-4">
          <Campo
            id="correo" etiqueta="Correo" type="email" autoComplete="email" required
            value={correo} onChange={(e) => setCorreo(e.target.value)}
          />
          <CampoClave
            id="clave" etiqueta="Contraseña" autoComplete="current-password"
            valor={clave} alCambiar={setClave} ver={ver} alVer={setVer}
          />
        </div>

        {error && <div className="mt-4"><Aviso tono="peligro">{error}</Aviso></div>}

        <Boton type="submit" tono="marca" disabled={entrando || !correo || !clave}
               className="mt-6 w-full">
          {entrando ? "Entrando…" : "Entrar"}
        </Boton>

        <p className="mt-4 text-center">
          <Link
            to={`/recuperar${correo ? `?correo=${encodeURIComponent(correo)}` : ""}`}
            className="text-[13px] text-tinta-2 hover:text-marca transition"
          >
            Olvidé mi contraseña
          </Link>
        </p>
      </form>
    </MarcoAuth>
  );
}
